import { format } from "date-fns";
import { ExternalLink } from "lucide-react";
import type { ReactNode, SyntheticEvent } from "react";
import { useImmer } from "use-immer";

import { type AgenticLogicResultItemStamp } from "@intelligems/ig-types/functions/agentic-logic/types/surface-render";
import { type AgenticLogicRuleRunCapture } from "@intelligems/ig-types/functions/agentic-logic/runs/capture";
import { type AgenticLogicRuleRunDto } from "@intelligems/ig-types/dtos/agentic-logic/rule-runs";
import { AgenticLogicRuleRunDtoDescriptions } from "@intelligems/ig-types/descriptions/agentic-logic.describe";
import {
  type AgenticLogicRuleRunListItemDto,
  type AgenticLogicRuleRunLogDto,
} from "@intelligems/ig-types/functions/agentic-logic/runs/list";
import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgChip } from "@intelligems/ig-theme/IgChip";
import { IgCodeEditor } from "@intelligems/ig-theme/IgCodeEditor";
import { IgCopyTextTypography } from "@intelligems/ig-theme/IgCopyTextTypography";
import { IgDialog } from "@intelligems/ig-theme/IgDialog";
import { IgTab } from "@intelligems/ig-theme/IgTab";
import { IgTabs } from "@intelligems/ig-theme/IgTabs";
import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { useAgenticLogicRuleRun } from "@src/api/agentic-logic/get-agentic-logic-rule-run";

import {
  agenticLogicRunDatadogLinks,
  type DatadogRunLogLink,
} from "./datadogRunLinks";
import {
  formatReadCache,
  RUN_FIELD_GROUPS,
  type RunField,
  runFieldLabel,
} from "./runFieldGroups";

interface RunDetailDialogProps {
  ruleId: string;
  run: AgenticLogicRuleRunListItemDto | null;
  onClose: () => void;
}

const MONO_FONT = '"JetBrains Mono", monospace';
type RunDetailView = "telemetry" | "input" | "output" | "event" | "logs";

/** The panel sections in tab order; `logs` is appended with its count. */
const RUN_DETAIL_VIEWS: ReadonlyArray<{
  value: Exclude<RunDetailView, "logs">;
  label: string;
}> = [
  { value: "telemetry", label: "Telemetry" },
  { value: "input", label: "Input" },
  { value: "output", label: "Output" },
  { value: "event", label: "Event" },
];

const formatValue = (field: RunField, value: unknown): string => {
  if (value === undefined || value === null || value === "") {
    return "—";
  }
  if (field === "evaluatedAtMs" && typeof value === "number") {
    return `${format(new Date(value), "PPpp")} (${value})`;
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (field === "readCache") {
    return formatReadCache(
      value as NonNullable<AgenticLogicRuleRunDto["readCache"]>
    );
  }
  return String(value);
};

const formatFallback = (
  fallback: AgenticLogicRuleRunDto["fallback"]
): string => {
  if (!fallback) {
    return "—";
  }
  const words = fallback.replaceAll("-", " ");
  return words.replace(/^./, (character) => character.toUpperCase());
};

const FieldRow = ({ field, value }: { field: RunField; value: unknown }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "minmax(112px, 0.8fr) minmax(0, 1.2fr)",
      gap: 1.5,
      py: 0.75,
      borderBottom: 1,
      borderColor: "divider",
    }}
  >
    <IgTooltip
      title={AgenticLogicRuleRunDtoDescriptions[field]}
      placement="left"
    >
      <IgTypography variant="body2" color="text.secondary">
        {runFieldLabel(field)}
      </IgTypography>
    </IgTooltip>
    <IgCopyTextTypography
      text={formatValue(field, value)}
      typographyProps={{
        variant: "body2",
        sx: { fontFamily: MONO_FONT, wordBreak: "break-word" },
        "aria-label": `${runFieldLabel(field)} value`,
      }}
    />
  </Box>
);

/**
 * One product the run returned. The GID is what an operator pastes into
 * Shopify or a support thread, so it is the copyable value; the variant and
 * the producing branch ride beside it because they are what explain WHY this
 * product is in the list and which slot it would have filled.
 */
const ReturnedProduct = ({
  item,
  position,
}: {
  item: AgenticLogicResultItemStamp;
  position: number;
}) => (
  <Box
    component="li"
    sx={{
      listStyle: "none",
      display: "grid",
      gridTemplateColumns: "auto minmax(0, 1fr)",
      gap: 1.5,
      alignItems: "baseline",
      py: 1,
      borderBottom: 1,
      borderColor: "divider",
    }}
  >
    <IgTypography
      variant="caption"
      color="text.secondary"
      sx={{ fontFamily: MONO_FONT }}
    >
      {position}
    </IgTypography>
    <Box sx={{ minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ minWidth: 0 }}
      >
        <IgChip size="small" label={item.kind} />
        <IgCopyTextTypography
          text={item.id}
          typographyProps={{
            variant: "body2",
            "aria-label": `Returned product ${position} ID`,
            sx: { fontFamily: MONO_FONT, wordBreak: "break-all" },
          }}
        />
      </Stack>
      {item.defaultVariantId !== undefined ||
      item.sourceBranch !== undefined ? (
        <IgTypography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5, fontFamily: MONO_FONT }}
        >
          {[
            item.defaultVariantId
              ? `variant ${item.defaultVariantId}`
              : undefined,
            item.sourceBranch ? `branch ${item.sourceBranch}` : undefined,
          ]
            .filter(Boolean)
            .join(" · ")}
        </IgTypography>
      ) : null}
    </Box>
  </Box>
);

/**
 * What the run actually returned, in envelope order.
 *
 * Three distinct states, kept distinct: a list, an empty list (a real and
 * common answer — an `empty` outcome, or a run whose items the placement's
 * onEmpty policy replaced), and no list at all, which only a row recorded
 * before the field existed can be. Collapsing the last two would report a run
 * that returned products as one that returned none.
 */
const ReturnedProductsBody = ({
  items,
}: {
  items: readonly AgenticLogicResultItemStamp[] | undefined;
}) => {
  if (items === undefined) {
    return (
      <IgTypography variant="body2" color="text.secondary">
        Not recorded for this run.
      </IgTypography>
    );
  }
  if (items.length === 0) {
    return (
      <IgTypography variant="body2" color="text.secondary">
        This run returned no products.
      </IgTypography>
    );
  }
  return (
    <Box component="ol" sx={{ p: 0, m: 0 }}>
      {items.map((item, index) => (
        <ReturnedProduct
          key={`${item.id}-${index}`}
          item={item}
          position={index + 1}
        />
      ))}
    </Box>
  );
};

const ReturnedProducts = ({
  items,
}: {
  items: readonly AgenticLogicResultItemStamp[] | undefined;
}) => (
  <Box>
    <IgTypography variant="subtitle2" sx={{ mb: 0.75 }}>
      Products returned{items ? ` (${items.length})` : ""}
    </IgTypography>
    <ReturnedProductsBody items={items} />
  </Box>
);

const SummaryMetric = ({ label, value }: { label: string; value: string }) => (
  <Stack component="div" spacing={0.5}>
    <IgTypography
      component="dt"
      variant="caption"
      color="text.secondary"
      sx={{ display: "block" }}
    >
      {label}
    </IgTypography>
    <IgCopyTextTypography
      text={value}
      typographyProps={{
        component: "dd",
        variant: "subtitle2",
        sx: { display: "block", m: 0 },
        "aria-label": `${label} value`,
      }}
    />
  </Stack>
);

const LogEntry = ({ entry }: { entry: AgenticLogicRuleRunLogDto }) => (
  <Box
    component="li"
    sx={{
      listStyle: "none",
      py: 1.5,
      borderBottom: 1,
      borderColor: "divider",
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="baseline">
      <IgTypography
        variant="caption"
        color="text.secondary"
        sx={{ fontFamily: MONO_FONT, flexShrink: 0 }}
      >
        {format(new Date(entry.timestamp), "HH:mm:ss.SSS")}
      </IgTypography>
      <IgTypography
        variant="caption"
        color={entry.level === "error" ? "error.main" : "text.secondary"}
        sx={{
          width: 44,
          flexShrink: 0,
          fontFamily: MONO_FONT,
          textTransform: "uppercase",
        }}
      >
        {entry.level}
      </IgTypography>
      <IgTypography variant="body2" sx={{ wordBreak: "break-word" }}>
        {entry.message}
      </IgTypography>
    </Stack>
    {entry.details !== undefined ? (
      <Box component="details" sx={{ mt: 1, ml: { sm: 10.5 } }}>
        <IgTypography
          component="summary"
          variant="caption"
          color="primary.main"
          sx={{ cursor: "pointer", width: "fit-content" }}
        >
          Show details
        </IgTypography>
        <Box
          component="pre"
          sx={{
            m: 0,
            mt: 1,
            p: 1.5,
            overflow: "auto",
            border: 1,
            borderColor: "divider",
            bgcolor: "action.hover",
            fontFamily: MONO_FONT,
            fontSize: 12,
            lineHeight: 1.6,
          }}
        >
          {JSON.stringify(entry.details, null, 2)}
        </Box>
      </Box>
    ) : null}
  </Box>
);

/**
 * One JSON object in the read-only code viewer, with the viewer's copy
 * control labelled after the section so a tab with several blocks says
 * what each one copies.
 */
const JsonBlock = ({
  title,
  value,
  caption,
}: {
  title: string;
  value: unknown;
  caption?: string;
}) => (
  <Box component="section" aria-label={title}>
    <IgCodeEditor
      language="json"
      readOnly
      editable={false}
      value={JSON.stringify(value, null, 2)}
      maxHeight="360px"
      copy={`Copy ${title} JSON`}
      title={
        <>
          <IgTypography variant="subtitle2" color="text.primary">
            {title}
          </IgTypography>
          {caption ? (
            <IgTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              {caption}
            </IgTypography>
          ) : null}
        </>
      }
    />
  </Box>
);

/**
 * The three capture views share one empty state: the payloads are recorded
 * to the live store only while Recent runs is listening, and a row read back
 * from the recorded trail carries the telemetry alone.
 */
const CaptureUnavailable = ({ loading }: { loading: boolean }) => (
  <Stack
    spacing={0.5}
    sx={{ px: 2.5, py: 2, bgcolor: "action.hover", borderRadius: 1 }}
  >
    <IgTypography variant="subtitle2">
      {loading ? "Loading capture…" : "Not captured for this run"}
    </IgTypography>
    <IgTypography variant="body2" color="text.secondary">
      {loading
        ? "Fetching the input and output objects the broker recorded."
        : "Input and output objects are recorded only for runs that happen while Recent runs is listening. Runs read back from the recorded trail carry telemetry alone."}
    </IgTypography>
  </Stack>
);

const CaptureInputView = ({
  capture,
}: {
  capture: AgenticLogicRuleRunCapture;
}) => (
  <Stack spacing={3}>
    <JsonBlock
      title="Invocation"
      caption="request + context + reserved candidates, exactly as the tier received them (secrets stripped)"
      value={capture.input.invocation}
    />
    <JsonBlock
      title="Runtime system"
      caption="the broker-minted runtime block (capability stripped)"
      value={capture.input.system}
    />
    <JsonBlock
      title="Caller context"
      caption="the surface-normalized shopper facts the broker trusted (buyer IP never recorded)"
      value={capture.input.callerContext}
    />
    <JsonBlock
      title="Rule settings"
      caption="frozen with the version that ran"
      value={capture.input.settings}
    />
    <JsonBlock
      title="Compute"
      caption="the tier and the Lambda that ran it"
      value={capture.input.compute}
    />
  </Stack>
);

const CaptureOutputView = ({
  capture,
}: {
  capture: AgenticLogicRuleRunCapture;
}) => (
  <Stack spacing={3}>
    <JsonBlock
      title="Shipped result"
      caption="what the surface received: envelope + evaluation telemetry after exclusions, in-cart filtering and the onEmpty policy"
      value={capture.output}
    />
    {capture.ruleResult ? (
      <JsonBlock
        title="Raw rule result"
        caption="what the tier returned before serving policy; differs from the shipped result when policy removed or replaced items"
        value={capture.ruleResult}
      />
    ) : (
      <Box>
        <IgTypography variant="subtitle2">Raw rule result</IgTypography>
        <IgTypography variant="body2" color="text.secondary">
          The tier returned nothing: the evaluation failed before a result came
          back.
        </IgTypography>
      </Box>
    )}
  </Stack>
);

const DatadogLinkButton = ({ link }: { link: DatadogRunLogLink }) => (
  <IgTooltip title={link.description} placement="top">
    <IgButton
      size="small"
      variant="outlined"
      color="inherit"
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      endIcon={<ExternalLink size={12} />}
      sx={{ whiteSpace: "nowrap" }}
    >
      {link.label}
    </IgButton>
  </IgTooltip>
);

export const RunDetailDialog = ({
  ruleId,
  run,
  onClose,
}: RunDetailDialogProps) => {
  const [activeView, setActiveView] = useImmer<RunDetailView>("telemetry");
  const detail = useAgenticLogicRuleRun(
    ruleId,
    run?.invocationId ?? null,
    run ?? undefined
  );
  const shown = detail.data?.run ?? run;
  const logs = detail.data?.run.logs ?? run?.logs ?? [];
  const capture = detail.data?.run.capture;
  const logsTabLabel = `Agent logs (${logs.length})`;
  const datadogLinks = shown
    ? agenticLogicRunDatadogLinks(shown, {
        stage: import.meta.env.VITE_STAGE,
        capture,
      })
    : [];

  const handleViewChange = (_event: SyntheticEvent, nextView: RunDetailView) =>
    setActiveView(nextView);

  const handleClose = () => {
    setActiveView("telemetry");
    onClose();
  };

  const captureBody = (
    render: (capture: AgenticLogicRuleRunCapture) => ReactNode
  ) =>
    capture ? (
      render(capture)
    ) : (
      <CaptureUnavailable loading={detail.isFetching && !detail.isError} />
    );

  const content = shown ? (
    <Stack
      sx={{
        height: "min(720px, calc(100vh - 48px))",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3, pb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IgTypography id="run-detail-title" variant="h3" component="h2">
            Run details
          </IgTypography>
          <IgChip
            size="small"
            color={
              shown.outcome === "result"
                ? "success"
                : shown.outcome === "error"
                  ? "error"
                  : "default"
            }
            label={shown.outcome === "result" ? "Result" : shown.outcome}
            sx={{ textTransform: "capitalize" }}
          />
        </Stack>
        <Box sx={{ mt: 1.5, minWidth: 0 }}>
          <IgCopyTextTypography
            text={shown.invocationId}
            showIcon
            typographyProps={{
              variant: "body1",
              "aria-label": "Run ID",
              sx: {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontFamily: MONO_FONT,
              },
            }}
          />
        </Box>
        <IgTypography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {format(new Date(shown.evaluatedAtMs), "PPpp")} · {shown.surface} ·{" "}
          {shown.evaluationSource}
        </IgTypography>
      </Box>

      <Box
        component="dl"
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(4, minmax(0, 1fr))",
          },
          gap: 2,
          m: 0,
          px: { xs: 2, sm: 3 },
          py: 2,
          borderTop: 1,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "action.hover",
        }}
      >
        <SummaryMetric label="Duration" value={`${shown.durationMs} ms`} />
        <SummaryMetric
          label="Version"
          value={shown.ruleVersion ? `v${shown.ruleVersion}` : "—"}
        />
        <SummaryMetric label="Items" value={String(shown.itemCount)} />
        <SummaryMetric
          label="Fallback"
          value={formatFallback(shown.fallback)}
        />
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        role="group"
        aria-label="Datadog logs"
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.25,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <IgTypography
          variant="caption"
          color="text.secondary"
          sx={{ mr: 0.5, flexShrink: 0 }}
        >
          Datadog logs
        </IgTypography>
        {datadogLinks.map((link) => (
          <DatadogLinkButton key={link.key} link={link} />
        ))}
      </Stack>

      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <IgTabs
          aria-label="Run detail sections"
          value={activeView}
          onChange={handleViewChange}
          sx={{ minHeight: 44 }}
        >
          {RUN_DETAIL_VIEWS.map((view) => (
            <IgTab
              key={view.value}
              id={`run-detail-tab-${view.value}`}
              aria-controls={`run-detail-panel-${view.value}`}
              value={view.value}
              label={view.label}
            />
          ))}
          <IgTab
            id="run-detail-tab-logs"
            aria-controls="run-detail-panel-logs"
            value="logs"
            label={logsTabLabel}
          />
        </IgTabs>
      </Box>

      <Box
        id={`run-detail-panel-${activeView}`}
        role="tabpanel"
        aria-labelledby={`run-detail-tab-${activeView}`}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          p: { xs: 2, sm: 3 },
        }}
      >
        {activeView === "telemetry" ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr" },
              gap: { xs: 4, md: 5 },
            }}
          >
            <Box>
              <IgTypography variant="subtitle2" sx={{ mb: 1 }}>
                {RUN_FIELD_GROUPS[0].title}
              </IgTypography>
              {RUN_FIELD_GROUPS[0].fields.map((field) => (
                <FieldRow key={field} field={field} value={shown[field]} />
              ))}
            </Box>
            <Stack spacing={4}>
              {RUN_FIELD_GROUPS.slice(1).map((group) => (
                <Box key={group.title}>
                  <IgTypography variant="subtitle2" sx={{ mb: 0.75 }}>
                    {group.title}
                  </IgTypography>
                  {group.fields.map((field) => (
                    <FieldRow key={field} field={field} value={shown[field]} />
                  ))}
                </Box>
              ))}
              <ReturnedProducts items={shown.items} />
            </Stack>
          </Box>
        ) : null}

        {activeView === "input"
          ? captureBody((captured) => <CaptureInputView capture={captured} />)
          : null}

        {activeView === "output"
          ? captureBody((captured) => <CaptureOutputView capture={captured} />)
          : null}

        {activeView === "event"
          ? captureBody((captured) => (
              <JsonBlock
                title="al_telemetry event"
                caption="the event exactly as the broker published it to the track bus; the Telemetry tab is this object minus its routing keys"
                value={captured.telemetryEvent}
              />
            ))
          : null}

        {activeView === "logs" ? (
          <>
            <IgTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Captured console output for this invocation.
            </IgTypography>

            {logs.length === 0 ? (
              <Stack
                spacing={0.5}
                sx={{
                  px: 2.5,
                  py: 2,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                }}
              >
                <IgTypography variant="subtitle2">
                  No agent logs found
                </IgTypography>
                <IgTypography variant="body2" color="text.secondary">
                  This run did not emit captured console output.
                </IgTypography>
              </Stack>
            ) : (
              <Box component="ol" sx={{ p: 0, m: 0 }}>
                {logs.map((entry) => (
                  <LogEntry key={entry.id} entry={entry} />
                ))}
              </Box>
            )}
          </>
        ) : null}
      </Box>
    </Stack>
  ) : null;

  return (
    <IgDialog
      showDialog={run !== null}
      title=""
      aria-labelledby="run-detail-title"
      handleClose={handleClose}
      hideActions
      showCloseIcon
      maxWidth="md"
      fullWidth
      minWidth="min(760px, calc(100vw - 32px))"
      dialogHeight={null}
      padding="0"
      rawContent={content}
      dontUseDialogContent
      scroll="paper"
    />
  );
};
